import { useEffect, useState } from 'react';

import { ADD_FILES } from '../utils/constants';
import FilesApi from '../api/filesApi';
import NoDataMessage from './NoDataMessage';
import SongFile from './SongFile';
import SongFileUpload from './SongFileUpload';
import { reportError } from '../utils/error';
import { selectCurrentMember } from '../store/authSlice';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import type { SongFile as SongFileModel } from '../types';

type SongFilesTabProps = {
  /** Called with the song's files once they load, and after each change. */
  onFilesChange: (files: SongFileModel[] | undefined) => void;
  /** Undefined until they load. */
  files?: SongFileModel[];
};

export default function SongFilesTab({
  onFilesChange,
  files,
}: SongFilesTabProps) {
  const [loading, setLoading] = useState(false);
  const { id: songId } = useParams<{ id: string }>();
  // Non-null: Content renders the pages only once the membership loads.
  const currentMember = useSelector(selectCurrentMember)!;

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const { data } = await FilesApi.getFilesForSong(songId);
        onFilesChange(data);
      } catch (error) {
        reportError(error);
      } finally {
        setLoading(false);
      }
    }

    if (!files) {
      fetchData();
    }
  }, [files, songId, onFilesChange]);

  function handleDelete(fileIdToDelete: number) {
    onFilesChange(files?.filter(file => file.id !== fileIdToDelete));
  }

  function handleUpdate(updatedFile: SongFileModel) {
    onFilesChange(
      // Non-null: only the SongFiles rendered from files call this.
      files!.map(file => (file.id === updatedFile.id ? updatedFile : file))
    );
  }

  return (
    <div>
      {currentMember.can(ADD_FILES) && (
        <SongFileUpload onFilesUploaded={onFilesChange} />
      )}

      {loading || files?.length === 0 ? (
        <NoDataMessage compact loading={loading} type="files" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {files?.map(file => (
            <SongFile
              key={file.id}
              file={file}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
